import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Calendar, Phone, Mail, User, Search, Filter, Bed,
  ExternalLink, Trash2, CheckCircle, Clock, XCircle, PhoneCall,
  RefreshCw, ChevronDown, Users, MessageSquare
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { format, parseISO, differenceInDays } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  new: { label: 'New', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300', icon: Clock },
  contacted: { label: 'Contacted', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300', icon: PhoneCall },
  confirmed: { label: 'Confirmed', color: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300', icon: CheckCircle },
  completed: { label: 'Completed', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300', icon: XCircle },
};

export default function AdminBookingInquiries() {
  const { token } = useAuth();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deleteId, setDeleteId] = useState(null);
  const [editInquiry, setEditInquiry] = useState(null);
  const [editNotes, setEditNotes] = useState('');
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, confirmed: 0 });

  const fetchInquiries = async () => {
    try {
      setLoading(true);
      const [inquiriesRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/booking-inquiries`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/admin/booking-inquiries/stats/summary`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { total: 0, new: 0, contacted: 0, confirmed: 0 } }))
      ]);
      setInquiries(inquiriesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch inquiries:', error);
      toast.error('Failed to load booking inquiries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInquiries();
  }, [token]);

  const handleStatusUpdate = async (inquiryId, newStatus) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/booking-inquiries/${inquiryId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInquiries(inquiries.map(inq => 
        inq.id === inquiryId ? { ...inq, status: newStatus } : inq
      ));
      toast.success(`Status updated to ${STATUS_CONFIG[newStatus].label}`);
      fetchInquiries(); // Refresh stats
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleNotesUpdate = async () => {
    if (!editInquiry) return;
    
    try {
      await axios.put(
        `${API_URL}/api/admin/booking-inquiries/${editInquiry.id}`,
        { notes: editNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInquiries(inquiries.map(inq => 
        inq.id === editInquiry.id ? { ...inq, notes: editNotes } : inq
      ));
      toast.success('Notes updated');
      setEditInquiry(null);
    } catch (error) {
      console.error('Failed to update notes:', error);
      toast.error('Failed to update notes');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/booking-inquiries/${deleteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInquiries(inquiries.filter(inq => inq.id !== deleteId));
      toast.success('Inquiry deleted');
      fetchInquiries(); // Refresh stats
    } catch (error) {
      console.error('Failed to delete inquiry:', error);
      toast.error('Failed to delete inquiry');
    } finally {
      setDeleteId(null);
    }
  };

  const filteredInquiries = inquiries.filter(inq => {
    const matchesSearch = 
      inq.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inq.mobile?.includes(searchTerm) ||
      inq.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || inq.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), 'MMM dd, yyyy');
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr) => {
    try {
      return format(parseISO(dateStr), 'MMM dd, yyyy h:mm a');
    } catch {
      return dateStr;
    }
  };

  const getNights = (checkIn, checkOut) => {
    try {
      return differenceInDays(parseISO(checkOut), parseISO(checkIn));
    } catch {
      return '-';
    }
  };

  return (
    <div className="p-6" data-testid="admin-booking-inquiries">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-serif text-foreground">Booking Inquiries</h1>
          <p className="text-sm text-muted-foreground">
            Manage direct booking requests from guests
          </p>
        </div>
        <Button variant="outline" onClick={fetchInquiries} className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-card border rounded-lg p-4">
          <p className="text-sm text-muted-foreground">Total Inquiries</p>
          <p className="text-2xl font-serif text-foreground">{stats.total}</p>
        </div>
        <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <p className="text-sm text-blue-600 dark:text-blue-400">New</p>
          <p className="text-2xl font-serif text-blue-700 dark:text-blue-300">{stats.new}</p>
        </div>
        <div className="bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <p className="text-sm text-yellow-600 dark:text-yellow-400">Contacted</p>
          <p className="text-2xl font-serif text-yellow-700 dark:text-yellow-300">{stats.contacted}</p>
        </div>
        <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <p className="text-sm text-green-600 dark:text-green-400">Confirmed</p>
          <p className="text-2xl font-serif text-green-700 dark:text-green-300">{stats.confirmed}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, mobile, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="contacted">Contacted</SelectItem>
            <SelectItem value="confirmed">Confirmed</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Inquiries List */}
      {loading ? (
        <div className="text-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto text-muted-foreground" />
          <p className="text-muted-foreground mt-2">Loading inquiries...</p>
        </div>
      ) : filteredInquiries.length === 0 ? (
        <div className="text-center py-12 bg-muted/50 rounded-lg">
          <Calendar className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">No booking inquiries found</p>
          {(searchTerm || statusFilter !== 'all') && (
            <Button 
              variant="link" 
              onClick={() => { setSearchTerm(''); setStatusFilter('all'); }}
            >
              Clear filters
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredInquiries.map((inquiry, index) => {
            const StatusIcon = STATUS_CONFIG[inquiry.status]?.icon || Clock;
            const nights = getNights(inquiry.check_in, inquiry.check_out);
            
            return (
              <motion.div
                key={inquiry.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                className="bg-card border rounded-lg p-4 sm:p-5"
              >
                <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                  {/* Main Info */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-medium text-foreground text-lg">{inquiry.name}</h3>
                          <Badge className={STATUS_CONFIG[inquiry.status]?.color}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {STATUS_CONFIG[inquiry.status]?.label}
                          </Badge>
                        </div>
                        <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                          <a href={`tel:${inquiry.mobile}`} className="flex items-center gap-1 hover:text-emerald-600">
                            <Phone className="w-3 h-3" />
                            {inquiry.mobile}
                          </a>
                          <a href={`mailto:${inquiry.email}`} className="flex items-center gap-1 hover:text-emerald-600">
                            <Mail className="w-3 h-3" />
                            {inquiry.email}
                          </a>
                        </div>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {formatDateTime(inquiry.created_at)}
                      </span>
                    </div>

                    {/* Booking Details */}
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm bg-muted/50 rounded-lg p-3">
                      <div>
                        <p className="text-muted-foreground text-xs uppercase tracking-wider mb-0.5">Check-in</p>
                        <p className="text-foreground font-medium">{formatDate(inquiry.check_in)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs uppercase tracking-wider mb-0.5">Check-out</p>
                        <p className="text-foreground font-medium">{formatDate(inquiry.check_out)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs uppercase tracking-wider mb-0.5">Duration</p>
                        <p className="text-foreground font-medium">{nights} {nights === 1 ? 'Night' : 'Nights'}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs uppercase tracking-wider mb-0.5">Guests</p>
                        <p className="text-foreground font-medium">
                          {inquiry.adults} Adult{inquiry.adults > 1 ? 's' : ''}
                          {inquiry.children > 0 && `, ${inquiry.children} Child${inquiry.children > 1 ? 'ren' : ''}`}
                        </p>
                      </div>
                    </div>

                    {/* Room & Requests */}
                    <div className="mt-3 flex flex-wrap gap-4 text-sm">
                      {inquiry.room_preference && (
                        <span className="flex items-center gap-1 text-foreground">
                          <Bed className="w-3 h-3 text-muted-foreground" />
                          {inquiry.room_preference}
                        </span>
                      )}
                      {inquiry.special_requests && (
                        <span className="flex items-center gap-1 text-muted-foreground">
                          <MessageSquare className="w-3 h-3" />
                          {inquiry.special_requests.substring(0, 50)}{inquiry.special_requests.length > 50 ? '...' : ''}
                        </span>
                      )}
                      {inquiry.source && (
                        <Badge variant="outline" className="text-xs">
                          Source: {inquiry.source}
                        </Badge>
                      )}
                    </div>

                    {/* Notes */}
                    {inquiry.notes && (
                      <div className="mt-3 p-2 bg-yellow-50 dark:bg-yellow-950/20 rounded border border-yellow-200 dark:border-yellow-800">
                        <p className="text-xs text-yellow-800 dark:text-yellow-200">
                          <strong>Notes:</strong> {inquiry.notes}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 lg:flex-col lg:items-end">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="gap-1">
                          Status
                          <ChevronDown className="w-3 h-3" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {Object.entries(STATUS_CONFIG).map(([status, config]) => (
                          <DropdownMenuItem
                            key={status}
                            onClick={() => handleStatusUpdate(inquiry.id, status)}
                            className="gap-2"
                          >
                            <config.icon className="w-4 h-4" />
                            {config.label}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>

                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => { setEditInquiry(inquiry); setEditNotes(inquiry.notes || ''); }}
                    >
                      <MessageSquare className="w-4 h-4" />
                    </Button>
                    
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setDeleteId(inquiry.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Inquiry</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this booking inquiry? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Notes Dialog */}
      <Dialog open={!!editInquiry} onOpenChange={() => setEditInquiry(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Notes for {editInquiry?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              placeholder="Add internal notes about this inquiry..."
              value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
              rows={4}
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditInquiry(null)}>
                Cancel
              </Button>
              <Button onClick={handleNotesUpdate} className="btn-emerald">
                Save Notes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
